package com.partejar.app.feature.relacionamentos.presentation

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.semantics.heading
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.dp
import com.partejar.app.core.format.formatBrazilianDateTime
import com.partejar.app.core.ui.components.PartejarButton
import com.partejar.app.core.ui.components.PartejarButtonSize
import com.partejar.app.core.ui.components.PartejarButtonVariant
import com.partejar.app.core.ui.theme.PartejarShapes
import com.partejar.app.core.ui.theme.PartejarTheme
import com.partejar.app.feature.relacionamentos.data.Interaction
import com.partejar.app.feature.relacionamentos.data.StageHistory
import kotlinx.datetime.LocalDateTime

internal sealed interface RelationshipHistoryEntry {
    val occurredAt: String

    data class Conversation(val interaction: Interaction) : RelationshipHistoryEntry {
        override val occurredAt: String = interaction.occurredAt
    }

    data class StageChange(val history: StageHistory) : RelationshipHistoryEntry {
        override val occurredAt: String = history.startedAt
    }
}

/** Conversations and stage changes in one story, newest first. ISO timestamps sort chronologically. */
internal fun relationshipHistory(interactions: List<Interaction>, stages: List<StageHistory>): List<RelationshipHistoryEntry> =
    (interactions.map(RelationshipHistoryEntry::Conversation) + stages.map(RelationshipHistoryEntry::StageChange))
        .sortedByDescending(RelationshipHistoryEntry::occurredAt)

private const val COLLAPSED_HISTORY_SIZE = 4

@Composable
internal fun RelationshipHistory(interactions: List<Interaction>, stages: List<StageHistory>, now: LocalDateTime) {
    val spacing = PartejarTheme.spacing
    val entries = relationshipHistory(interactions, stages)
    var expanded by rememberSaveable { mutableStateOf(false) }
    val visible = if (expanded) entries else entries.take(COLLAPSED_HISTORY_SIZE)

    Column(verticalArrangement = Arrangement.spacedBy(spacing.space12)) {
        Row(verticalAlignment = Alignment.Bottom) {
            Text(
                "Histórico",
                style = MaterialTheme.typography.titleLarge,
                modifier = Modifier.weight(1f).semantics { heading() }
            )
            Text(
                "Mais recentes primeiro",
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
        if (entries.isEmpty()) {
            Text(
                "Nenhum registro ainda. As conversas que você registrar aparecem aqui.",
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        } else {
            Column {
                visible.forEachIndexed { index, entry ->
                    val last = index == visible.lastIndex
                    when (entry) {
                        is RelationshipHistoryEntry.Conversation -> {
                            val interaction = entry.interaction
                            val visual = interactionPresentation(interaction.type)
                            RelationshipTimelineItem(
                                icon = visual.icon,
                                title = visual.label,
                                meta = formatBrazilianDateTime(interaction.occurredAt) ?: interaction.occurredAt,
                                last = last
                            ) {
                                interaction.note?.takeIf(String::isNotBlank)?.let {
                                    Text(it, style = MaterialTheme.typography.bodyMedium)
                                }
                                interaction.nextFollowUp?.let { next ->
                                    Text(
                                        "Retorno marcado: ${relativeMoment(next, now) ?: next}",
                                        style = MaterialTheme.typography.bodySmall,
                                        color = MaterialTheme.colorScheme.primary
                                    )
                                }
                            }
                        }
                        is RelationshipHistoryEntry.StageChange -> {
                            val history = entry.history
                            RelationshipTimelineItem(
                                icon = stagePresentation(history.stage).icon,
                                title = "Etapa: ${stageTitle(history.stage)}",
                                meta = listOfNotNull(
                                    formatBrazilianDateTime(history.startedAt) ?: history.startedAt,
                                    history.changedBy?.takeIf(String::isNotBlank)
                                ).joinToString(" · "),
                                last = last,
                                muted = true
                            ) {
                                history.reason?.takeIf(String::isNotBlank)?.let {
                                    Text(it, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
                                }
                            }
                        }
                    }
                }
            }
            if (entries.size > COLLAPSED_HISTORY_SIZE) {
                PartejarButton(
                    text = if (expanded) "Mostrar menos" else "Ver histórico completo (${entries.size})",
                    onClick = { expanded = !expanded },
                    variant = PartejarButtonVariant.TERTIARY,
                    size = PartejarButtonSize.COMPACT,
                    fillWidth = false
                )
            }
        }
    }
}

@Composable
private fun RelationshipTimelineItem(
    icon: ImageVector,
    title: String,
    meta: String,
    last: Boolean,
    muted: Boolean = false,
    content: @Composable ColumnScope.() -> Unit
) {
    val spacing = PartejarTheme.spacing
    Row(
        modifier = Modifier.fillMaxWidth().height(IntrinsicSize.Min),
        horizontalArrangement = Arrangement.spacedBy(spacing.space12)
    ) {
        Column(horizontalAlignment = Alignment.CenterHorizontally) {
            Surface(
                shape = PartejarShapes.pill,
                color = if (muted) MaterialTheme.colorScheme.surfaceVariant else MaterialTheme.colorScheme.primaryContainer,
                contentColor = if (muted) MaterialTheme.colorScheme.onSurfaceVariant else MaterialTheme.colorScheme.onPrimaryContainer
            ) {
                Box(Modifier.size(PartejarTheme.dimensions.iconLarge), contentAlignment = Alignment.Center) {
                    Icon(icon, contentDescription = null, modifier = Modifier.size(PartejarTheme.dimensions.iconSmall))
                }
            }
            if (!last) {
                Box(
                    Modifier
                        .padding(vertical = spacing.space4)
                        .width(PartejarTheme.dimensions.strokeThin)
                        .weight(1f)
                        .background(MaterialTheme.colorScheme.outlineVariant)
                )
            }
        }
        Column(
            modifier = Modifier.weight(1f).padding(bottom = if (last) 0.dp else spacing.space20),
            verticalArrangement = Arrangement.spacedBy(spacing.space4)
        ) {
            Text(title, style = MaterialTheme.typography.titleSmall)
            content()
            Text(meta, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
        }
    }
}
